import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Share, Plus, Download } from "lucide-react";

const DISMISS_KEY = "eyb_install_dismissed";

const isStandalone = () =>
  window.matchMedia("(display-mode: standalone)").matches ||
  window.navigator.standalone === true;

const isIOSDevice = () =>
  /iphone|ipad|ipod/i.test(window.navigator.userAgent) && !window.MSStream;

export const InstallPrompt = () => {
  const [show, setShow] = useState(false);
  const [deferred, setDeferred] = useState(null);
  const [ios, setIos] = useState(false);

  useEffect(() => {
    if (localStorage.getItem(DISMISS_KEY) === "1") return;
    if (isStandalone()) return;

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferred(e);
      setShow(true);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);

    if (isIOSDevice()) setIos(true);

    // Show the first-visit prompt regardless of platform; if a native
    // install event arrives it upgrades this to a one-tap install button.
    const timer = setTimeout(() => setShow(true), 1800);

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      clearTimeout(timer);
    };
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, "1");
    setShow(false);
  };

  const install = async () => {
    if (deferred) {
      deferred.prompt();
      await deferred.userChoice;
      setDeferred(null);
    }
    dismiss();
  };

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          data-testid="install-prompt"
          className="absolute inset-0 z-[60] flex items-end justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <div
            className="absolute inset-0 bg-[#03112a]/60 backdrop-blur-sm"
            onClick={dismiss}
            data-testid="install-backdrop"
          />

          <motion.div
            className="relative m-3 w-full max-w-[400px] rounded-[28px] border border-white/15 bg-[#0a2547]/95 p-6 backdrop-blur-2xl"
            style={{ boxShadow: "0 -20px 60px rgba(0,0,0,0.5)" }}
            initial={{ y: 60, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 60, opacity: 0 }}
            transition={{ type: "spring", stiffness: 260, damping: 28 }}
          >
            <button
              type="button"
              onClick={dismiss}
              data-testid="install-dismiss-btn"
              aria-label="Dismiss"
              className="absolute right-4 top-4 grid h-8 w-8 place-items-center rounded-full bg-white/10 text-white/70 active:scale-90"
            >
              <X size={16} />
            </button>

            <div className="flex items-center gap-3">
              <img
                src="/eyb-logo.png"
                alt="EYB"
                className="h-14 w-14 object-contain"
                style={{ filter: "drop-shadow(0 6px 14px rgba(0,0,0,0.4))" }}
              />
              <div>
                <h3 className="font-heading text-lg font-semibold text-white">
                  Add EYB to your Home Screen
                </h3>
                <p className="text-xs text-white/60">
                  Get one-tap access to all your tools.
                </p>
              </div>
            </div>

            {deferred ? (
              <button
                type="button"
                onClick={install}
                data-testid="install-accept-btn"
                className="mt-5 flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#2EC1F0] to-[#1273C4] py-3 text-sm font-semibold text-white active:scale-[0.98]"
                style={{ boxShadow: "0 10px 24px rgba(46,193,240,0.4)" }}
              >
                <Download size={17} />
                Install EYB App
              </button>
            ) : (
              <div
                data-testid="install-instructions"
                className="mt-5 space-y-2 rounded-2xl bg-white/5 p-4"
              >
                <p className="flex items-center gap-2 text-sm text-white/80">
                  <span className="grid h-6 w-6 place-items-center rounded-md bg-white/10">
                    <Share size={14} className="text-[#2EC1F0]" />
                  </span>
                  1. Tap the{" "}
                  <b className="font-semibold">
                    {ios ? "Share" : "browser menu"}
                  </b>{" "}
                  button
                </p>
                <p className="flex items-center gap-2 text-sm text-white/80">
                  <span className="grid h-6 w-6 place-items-center rounded-md bg-white/10">
                    <Plus size={14} className="text-[#2EC1F0]" />
                  </span>
                  2. Choose{" "}
                  <b className="font-semibold">
                    {ios ? "Add to Home Screen" : "Install app / Add to Home Screen"}
                  </b>
                </p>
              </div>
            )}

            <button
              type="button"
              onClick={dismiss}
              data-testid="install-later-btn"
              className="mt-3 w-full text-center text-xs text-white/45"
            >
              Maybe later
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
